import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Componente para exibir página de acesso não autorizado
 * Utilizado quando usuário não tem permissão para acessar um recurso
 */
export default function UnauthorizedPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    // Obtém mensagem da query string se disponível
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');

    const getDisplayMessage = () => {
        switch (message) {
            case 'session_expired':
                return 'Sua sessão expirou. Por favor, faça login novamente.';
            case 'insufficient_permissions':
                return 'Você não tem permissão para acessar este recurso.';
            case 'account_disabled':
                return 'Sua conta foi desabilitada. Entre em contato com o administrador.';
            default:
                return 'Acesso não autorizado. Você não tem permissão para visualizar esta página.';
        }
    };

    const getMessageIcon = () => {
        switch (message) {
            case 'session_expired':
                return 'bi-clock';
            case 'account_disabled':
                return 'bi-person-x';
            default:
                return 'bi-shield-exclamation';
        }
    };

    const handleGoHome = () => {
        if (user) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    const handleLogin = () => {
        navigate('/login');
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleGoBack = () => {
        // Volta para a página anterior ou dashboard se não houver histórico
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            handleGoHome();
        }
    };

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-dark">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <div className="card bg-secondary border-0 shadow-lg">
                            <div className="card-body p-5 text-center">
                                {/* Ícone principal */}
                                <div className="mb-4">
                                    <i className={`bi ${getMessageIcon()} text-warning`} style={{ fontSize: '4rem' }}></i>
                                </div>

                                {/* Título */}
                                <h2 className="text-white mb-3">
                                    {message === 'session_expired' ? 'Sessão Expirada' : 'Acesso Negado'}
                                </h2>

                                {/* Mensagem principal */}
                                <p className="text-light mb-4">
                                    {getDisplayMessage()}
                                </p>

                                {/* Informações do usuário se logado */}
                                {user && (
                                    <div className="alert alert-info mb-4">
                                        <small>
                                            Logado como: <strong>{user.name}</strong><br />
                                            Perfil: <strong>{user.perfil?.nome || 'Não definido'}</strong>
                                        </small>
                                    </div>
                                )}

                                {/* Botões de ação */}
                                <div className="d-grid gap-3">
                                    {message === 'session_expired' || !user ? (
                                        <>
                                            {/* Se sessão expirada ou não logado, mostrar botão de login */}
                                            <button
                                                className="btn btn-primary btn-lg"
                                                onClick={handleLogin}
                                            >
                                                <i className="bi bi-box-arrow-in-right me-2"></i>
                                                Fazer Login
                                            </button>
                                            
                                            <button
                                                className="btn btn-outline-light"
                                                onClick={() => navigate('/')}
                                            >
                                                <i className="bi bi-house me-2"></i>
                                                Página Inicial
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Se logado mas sem permissão */}
                                            <button
                                                className="btn btn-primary btn-lg"
                                                onClick={handleGoHome}
                                            >
                                                <i className="bi bi-house me-2"></i>
                                                Ir para Painel
                                            </button>
                                            
                                            <button
                                                className="btn btn-outline-light"
                                                onClick={handleGoBack}
                                            >
                                                <i className="bi bi-arrow-left me-2"></i>
                                                Voltar
                                            </button>
                                            
                                            <div className="mt-3">
                                                <button
                                                    className="btn btn-link text-muted"
                                                    onClick={handleLogout}
                                                >
                                                    <i className="bi bi-box-arrow-right me-2"></i>
                                                    Sair do Sistema
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Link de contato para problemas de permissão */}
                                {user && message !== 'session_expired' && (
                                    <div className="mt-4">
                                        <hr className="text-muted" />
                                        <small className="text-muted">
                                            Se você acredita que deveria ter acesso a este recurso,<br />
                                            entre em contato com o administrador do sistema.
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}